import type { ReviewQuestion } from "@/types";
import CategoryBadge from "./CategoryBadge";
import MediaDisplay from "./MediaDisplay";
import AnswerOption from "./AnswerOption";

interface ExamReviewQuestionProps {
  reviewAnswer: ReviewQuestion;
  questionNumber: number;
  totalQuestions: number;
}

const ANSWER_LABELS = ["A", "B", "C", "D", "E", "F"];

export default function ExamReviewQuestion({
  reviewAnswer,
  questionNumber,
  totalQuestions,
}: ExamReviewQuestionProps) {
  const {
    category_name,
    question_text,
    explanation,
    image_url,
    video_url,
    has_image,
    has_video,
    answers,
    selected_answer_id,
    is_correct,
  } = reviewAnswer;

  return (
    <div
      className={`rounded-xl border-2 bg-white p-6 shadow-sm ${
        is_correct ? "border-success-200" : "border-danger-200"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-500">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
              is_correct
                ? "bg-success-100 text-success-800"
                : "bg-danger-100 text-danger-800"
            }`}
          >
            {is_correct ? "Correct" : "Incorrect"}
          </span>
        </div>
        <CategoryBadge name={category_name} />
      </div>

      <h3 className="text-lg font-semibold text-gray-900 leading-relaxed">
        {question_text}
      </h3>

      <MediaDisplay
        imageUrl={image_url}
        videoUrl={video_url}
        hasImage={has_image}
        hasVideo={has_video}
      />

      <div className="mt-6 space-y-3">
        {answers.map((answer, index) => (
          <AnswerOption
            key={answer.id}
            id={answer.id}
            text={answer.text}
            label={ANSWER_LABELS[index] || String(index + 1)}
            isSelected={selected_answer_id === answer.id}
            isCorrect={answer.is_correct}
            isIncorrect={!answer.is_correct}
            showResult={true}
            disabled={true}
            onSelect={() => {}}
          />
        ))}
      </div>

      <div className="mt-6 rounded-lg bg-primary-50 border border-primary-200 p-4">
        <h4 className="text-sm font-semibold text-primary-800 mb-1">Explanation</h4>
        <p className="text-sm text-primary-700">{explanation}</p>
      </div>
    </div>
  );
}
